<template>
  <div :style="style" class="toast tw:flex tw:items-start tw:gap-3 tw:p-4 tw:rounded-[8px] tw:shadow-xl tw:relative tw:overflow-hidden tw:cursor-pointer tw:min-w-80 tw:max-w-sm" @click="handleClick">
    <IconGoogle v-if="icon" :name="icon.name" :type="icon.type || 'outlined'" :color="color" size="1.25rem" class="tw:flex-shrink-0 tw:mt-0.5" />
    <div class="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:pr-4">
      <TextPlay v-if="title" :text="title" size="sm" weight="semibold" />
      <TextPlay v-if="message" :text="message" size="sm" color="var(--fontcolor2)" class="tw:whitespace-pre-line" />
    </div>
    <button class="toast__close" @click.prevent.stop="emit('close')">
      <IconGoogle name="cancel" color="var(--fontcolor2)" size="1rem" />
    </button>
    <div v-if="duration" class="toast__progress" />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue';
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'
const IconGoogle = defineAsyncComponent(() => import('../../atoms/IconGoogle/IconGoogle.vue'))

const emit = defineEmits<{
  (e: "close"): void;
}>();

const timeout = ref<NodeJS.Timeout | null>(null);

const props = withDefaults(defineProps<{
  icon?: {
    name: string;
    type: string;
  };
  title?: string;
  message?: string;
  color?: string;
  duration?: number;
  action?: () => void;
}>(), {
  duration: 5000,
  action: undefined,
  color: undefined,
  icon: undefined,
  message: undefined,
  title: undefined,
});

const animDuration = computed(() => `${props.duration}ms`);

const style = computed(() => ({
  '--color': props.color,
}));

function autoClose() {
  if (!props.duration) return;
  timeout.value = setTimeout(() => {
    emit('close');
  }, props.duration);
}

function handleClick() {
  emit('close');
  if (props.action) {
    props.action();
  }
}

onMounted(() => autoClose());
onUnmounted(() => clearTimeout(timeout.value));
</script>

<style scoped>
.toast {
  background-color: color-mix(in srgb, var(--color) 10%, var(--box-background-color));
  border: 1px solid color-mix(in srgb, var(--color) 25%, transparent);
}

.toast__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.toast__close:hover {
  opacity: 1;
}

.toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background-color: var(--color);
  animation: toast-progress v-bind(animDuration) linear forwards;
}

@keyframes toast-progress {
  from { width: 100%; }
  to { width: 0%; }
}
</style>
